import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
  expiresAt: string | null;
  invitedBy: {
    name: string;
    email: string;
  };
}

export const PendingInvitationsTab = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const notify = useNotify();

  const fetchInvitations = async () => {
    setLoading(true);
    setError('');

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');
      const currentTenant = JSON.parse(localStorage.getItem('currentTenant') || '{}');

      const response = await fetch(`${baseUrl}/tenant-invitations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-code': currentTenant.code || 'default',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invitations');
      }

      const data = await response.json();
      // Filter for pending invitations only
      const pendingInvitations = data.filter((inv: Invitation) => inv.status === 'pending');
      setInvitations(pendingInvitations);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const handleResend = async (invitationId: string) => {
    setResendingId(invitationId);

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');
      const currentTenant = JSON.parse(localStorage.getItem('currentTenant') || '{}');

      const response = await fetch(`${baseUrl}/tenant-invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-code': currentTenant.code || 'default',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to resend invitation');
      }

      notify('Invitation resent successfully!', { type: 'success' });
    } catch (err: any) {
      notify(err.message || 'Failed to resend invitation', { type: 'error' });
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = async (invitationId: string) => {
    if (!confirm('Are you sure you want to delete this invitation?')) {
      return;
    }

    setDeletingId(invitationId);

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');
      const currentTenant = JSON.parse(localStorage.getItem('currentTenant') || '{}');

      const response = await fetch(`${baseUrl}/tenant-invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-code': currentTenant.code || 'default',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete invitation');
      }

      notify('Invitation deleted successfully!', { type: 'success' });
      // Refresh the list
      await fetchInvitations();
    } catch (err: any) {
      notify(err.message || 'Failed to delete invitation', { type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (invitations.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No pending invitations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Invite users from the Team Members tab
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Pending Invitations ({invitations.length})
        </Typography>
        <IconButton onClick={fetchInvitations} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Invited By</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map((invitation) => (
              <TableRow key={invitation.id}>
                <TableCell>
                  <Typography variant="body2">{invitation.email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={invitation.role}
                    size="small"
                    variant="outlined"
                    color={invitation.role === 'admin' ? 'error' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {invitation.invitedBy.name || invitation.invitedBy.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(invitation.createdAt).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Resend invitation">
                    <IconButton
                      size="small"
                      onClick={() => handleResend(invitation.id)}
                      disabled={resendingId === invitation.id}
                    >
                      {resendingId === invitation.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SendIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete invitation">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(invitation.id)}
                      disabled={deletingId === invitation.id}
                      color="error"
                    >
                      {deletingId === invitation.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
